'use client';

import { useEffect, useMemo, useState } from 'react';
import { Sparkles, X, ArrowRight, HelpCircle, Play, RotateCcw, CheckCircle2 } from 'lucide-react';
import type { TourProgressState } from '@/config/guided-tours/types';

/**
 * TourPrompt
 *
 * A lightweight, self-contained proactive tour offer popup. Renders a small
 * mascot-style card in the bottom-right corner that invites the user to take
 * a guided tour of the current section/view.
 *
 * Unlike the full GuidedTourProvider, this component does NOT need to be
 * wrapped in a tour context. It simply dispatches a `guided-tour-open`
 * custom event with the configured moduleKey/flowId/stepId, which the
 * module-level <GuidedTour> instance picks up.
 *
 * Features:
 * - Completion-aware: fetches progress from the API and suppresses prompts
 *   for already-completed flows.
 * - Resume-aware: if the configured flow is in-progress, shows a "Resume"
 *   prompt instead of a new-tour prompt.
 * - Next-flow suggestion: if the configured flow is completed and
 *   `availableFlows` is provided, suggests the next uncompleted flow.
 * - Dismissal is persisted per-context in localStorage so the prompt does
 *   not reappear for the same section on every visit.
 */

export interface TourPromptFlowInfo {
  id: string;
  label: string;
  summary?: string;
}

export interface TourPromptConfig {
  /** The module key for the tour (e.g. 'total-finance', 'total-operations') */
  moduleKey: string;
  /** The flow ID to launch (e.g. 'sales-documents', 'inventory') */
  flowId: string;
  /** Optional: specific step ID to start at */
  stepId?: string;
  /** Optional: objective ID to start at */
  objectiveId?: string;
  /** Title shown in the prompt card. Defaults to "Take a quick tour?" */
  title?: string;
  /** Body text shown in the prompt card. */
  message?: string;
  /** Label for the accept button. Defaults to "Show me around" */
  actionLabel?: string;
}

interface TourPromptProps {
  /** Tour launch configuration. When null/undefined, the prompt is not shown. */
  config: TourPromptConfig | null;
  /**
   * A stable string identifying the context where the prompt appears.
   * Used as the localStorage key suffix so dismissal is scoped per-section.
   * Example: 'finance:sales', 'inventory:items'
   */
  contextKey: string;
  /** Delay (ms) before the prompt appears. Defaults to 1500ms. */
  delayMs?: number;
  /** When false, the prompt is suppressed (e.g. if user is not permitted). */
  enabled?: boolean;
  /**
   * Optional: list of all flows available in the current module. When
   * provided, the prompt can suggest the next uncompleted flow if the
   * configured flow is already done.
   */
  availableFlows?: TourPromptFlowInfo[];
}

const DISMISS_STORAGE_PREFIX = 'ta_tour_prompt_dismissed:';

function isDismissed(contextKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(`${DISMISS_STORAGE_PREFIX}${contextKey}`) === '1';
  } catch {
    return false;
  }
}

function setDismissed(contextKey: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(`${DISMISS_STORAGE_PREFIX}${contextKey}`, '1');
  } catch {
    // Ignore quota errors
  }
}

type PromptMode = 'new' | 'resume' | 'next-flow' | 'completed';

interface ResolvedPrompt {
  mode: PromptMode;
  config: TourPromptConfig;
  flowLabel?: string;
}

export function TourPrompt({
  config,
  contextKey,
  delayMs = 1500,
  enabled = true,
  availableFlows,
}: TourPromptProps) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissedState] = useState(() => isDismissed(contextKey));
  const [progress, setProgress] = useState<TourProgressState | null>(null);
  const [progressLoaded, setProgressLoaded] = useState(false);

  // Reset dismissal state when the context changes (different section).
  const [prevContextKey, setPrevContextKey] = useState(contextKey);
  if (prevContextKey !== contextKey) {
    setPrevContextKey(contextKey);
    setDismissedState(isDismissed(contextKey));
    setVisible(false);
    setProgress(null);
    setProgressLoaded(false);
  }

  // Fetch tour progress from the API to determine completion/resume state.
  const progressModuleKey = config?.moduleKey;
  useEffect(() => {
    if (!enabled || !progressModuleKey || typeof window === 'undefined') {
      setProgressLoaded(true);
      return;
    }

    let cancelled = false;

    const loadProgress = async () => {
      try {
        const response = await fetch(`/api/v1/guided-tours/progress/${progressModuleKey}`);
        if (!response.ok) {
          setProgressLoaded(true);
          return;
        }
        const data = await response.json();
        if (!cancelled && data?.success && data.progress) {
          setProgress({
            verified_step_ids: [],
            verified_step_timestamps: {},
            last_active_flow_id: null,
            last_active_objective_id: null,
            last_active_step_index: null,
            ...data.progress,
          });
        }
      } catch {
        // Progress fetch failed — proceed as if no progress
      } finally {
        if (!cancelled) {
          setProgressLoaded(true);
        }
      }
    };

    loadProgress();

    return () => {
      cancelled = true;
    };
  }, [enabled, progressModuleKey]);

  // Resolve the prompt mode based on progress.
  const resolvedPrompt = useMemo<ResolvedPrompt | null>(() => {
    if (!config) return null;

    if (!progress) {
      return { mode: 'new' as PromptMode, config };
    }

    const completedFlows = progress.completed_flow_ids ?? [];
    const isCompleted = completedFlows.includes(config.flowId);
    const isInProgress =
      progress.last_active_flow_id === config.flowId &&
      progress.last_active_step_index !== null &&
      progress.last_active_step_index > 0 &&
      !isCompleted;

    if (isCompleted) {
      // If we have available flows, suggest the next uncompleted one
      if (availableFlows && availableFlows.length > 0) {
        const nextFlow = availableFlows.find((f) => !completedFlows.includes(f.id));
        if (nextFlow) {
          return {
            mode: 'next-flow',
            config: { ...config, flowId: nextFlow.id },
            flowLabel: nextFlow.label,
          };
        }
      }
      return { mode: 'completed', config };
    }

    if (isInProgress) {
      return { mode: 'resume', config };
    }

    return { mode: 'new', config };
  }, [config, progress, availableFlows]);

  // Show the prompt after the configured delay, if not dismissed, enabled,
  // progress is loaded, and the flow isn't already completed.
  useEffect(() => {
    if (!enabled || !config || dismissed || !progressLoaded) {
      return;
    }

    // Suppress prompt if the flow is completed and there's no next flow.
    if (resolvedPrompt?.mode === 'completed') {
      return;
    }

    const timer = setTimeout(() => {
      setVisible(true);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [enabled, config, dismissed, delayMs, progressLoaded, resolvedPrompt]);

  // Hide the prompt when a tour is already open (listen for the open event).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleTourOpen = () => setVisible(false);
    window.addEventListener('guided-tour-open', handleTourOpen);

    return () => window.removeEventListener('guided-tour-open', handleTourOpen);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setDismissedState(true);
    setDismissed(contextKey);
  };

  const handleStart = () => {
    if (!resolvedPrompt || typeof window === 'undefined') return;
    setVisible(false);
    window.dispatchEvent(
      new CustomEvent('guided-tour-open', {
        detail: {
          moduleKey: resolvedPrompt.config.moduleKey,
          flowId: resolvedPrompt.config.flowId,
          stepId: resolvedPrompt.config.stepId,
          objectiveId: resolvedPrompt.config.objectiveId,
        },
      })
    );
  };

  const moduleColor = useMemo(() => '#6366f1', []);

  if (!visible || !config || !resolvedPrompt || resolvedPrompt.mode === 'completed') {
    return null;
  }

  const mode = resolvedPrompt.mode;
  const title =
    mode === 'resume'
      ? 'Pick up where you left off?'
      : mode === 'next-flow'
        ? `Ready for the next tour?`
        : config.title ?? 'Take a quick tour?';
  const message =
    mode === 'resume'
      ? `You were partway through a tour. Want to continue from where you stopped?`
      : mode === 'next-flow'
        ? `You've completed this section's tour. Up next: ${resolvedPrompt.flowLabel ?? 'another tour'}.`
        : config.message ?? 'I can show you the key features of this section in a minute or two.';
  const actionLabel =
    mode === 'resume'
      ? 'Resume tour'
      : mode === 'next-flow'
        ? `Start: ${resolvedPrompt.flowLabel ?? 'Next'}`
        : config.actionLabel ?? 'Show me around';
  const ActionIcon = mode === 'resume' ? RotateCcw : mode === 'next-flow' ? ArrowRight : Play;

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-[65] w-[min(92vw,22rem)] animate-[tour-card-slide_0.4s_ease-out_both]">
      <div
        className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95"
        style={{ borderTopColor: moduleColor, borderTopWidth: '3px' }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-2 top-2 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          title="Dismiss"
          aria-label="Dismiss tour prompt"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 p-4 pr-8">
          {/* Mascot icon */}
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: `${moduleColor}20` }}
          >
            {mode === 'next-flow' ? (
              <CheckCircle2 className="h-6 w-6" style={{ color: '#22c55e' }} />
            ) : (
              <Sparkles className="h-6 w-6" style={{ color: moduleColor }} />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <HelpCircle className="h-3.5 w-3.5 shrink-0" style={{ color: moduleColor }} />
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                {title}
              </h3>
            </div>
            <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              {message}
            </p>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleStart}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md"
                style={{ backgroundColor: moduleColor }}
              >
                {actionLabel}
                <ActionIcon className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
